<script setup lang="ts">
import { onBeforeMount, onMounted, ref, inject, Ref } from 'vue';
import { getCurrentLocale } from '@/i18n';
import { useI18n } from 'vue-i18n';
import { STATUS_OPEN, STATUS_CLOSED, NOT_CHECK_STATUS } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { getTextVariant } from '@/utils/stringUtils';
import { useViewRuleStore } from '@/store/ViewRuleStore';
import { useDialog } from 'primevue/usedialog';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { useRenderStatusComposable } from './useRenderStatusComposable';
import { useToast } from 'primevue/usetoast';
import AutoComplete from 'primevue/autocomplete';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import DataTable from 'primevue/datatable';
import TableColumn from 'primevue/column';
import Tab from 'primevue/tab';
import TabList from 'primevue/tablist';
import TabPanel from 'primevue/tabpanel';
import TabPanels from 'primevue/tabpanels';
import Tabs from 'primevue/tabs';
import Tag from 'primevue/tag';
import ToggleSwitch from 'primevue/toggleswitch';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import PreViewRuleModal from '@/components/modal/PreViewRuleModal.vue';

const dialogRef = inject('dialogRef') as Ref;
const rule = dialogRef?.value.data.ruleValue.rule;
const ruleView = dialogRef?.value.data.ruleValue.ruleView;
const quoteID = dialogRef?.value.data.ruleValue.sk;
const { t } = useI18n({ useScope: 'global' });
const viewRuleStore = useViewRuleStore();
const { renderStatusYesNoSpecial, renderClassSpecial } = useRenderStatusComposable();
const toast = useToast();
const expandedRows = ref([]);
const groupTypeManagement = useGroupTypeStore();
const groupTypeList = ref([]);
const ruleData = ref({});
const ruleViewData = ref({});
const dialog = useDialog();
const isLoading = ref(false);
const currentLanguage = getCurrentLocale();

onMounted(() => {
  if (rule.length > 0) {
    rule.forEach((option, index) => {
      expandedRows.value[index] = option.flightLog;
    });
  }
});

const getHotel = (p) => {
  let result = '';
  p.hotel.forEach((hotel, indexH) => {
    if (indexH > 0) {
      result += ',\n';
    }
    result += `${hotel.hotel}`;
  });
  return result;
};

const getRoom = (p) => {
  let result = '';
  p.hotel.forEach((hotel) => {
    hotel.roomCategory.forEach((room, indexR) => {
      if (indexR > 0) {
        result += ', ';
      }

      result += room;
    });
    result += '\n';
  });
  return result;
};

const renderStatus = (status: number) => {
  if (status === NOT_CHECK_STATUS) {
    return t('my_quote.not_checked');
  }
  return status ? t('my_quote.pass') : t('my_quote.fail');
};

const renderStatusYesNo = (status: number) => {
  if (status === NOT_CHECK_STATUS) {
    return t('my_quote.not_checked');
  }
  return status ? t('common.yes') : t('common.no');
};

const renderClass = (status: number) => {
  if (status === NOT_CHECK_STATUS) {
    return 'unchecked';
  }
  return status ? 'pass-status' : 'fail-status';
};

const getTagSeverity = (status: string) => {
  if (status === null) {
    return 'warning';
  }
  return status ? 'success' : 'danger';
};

const getTagValue = (status) => {
  if (status === null) {
    return t('my_quote.not_run');
  }
  return status ? t('my_quote.pass').toUpperCase() : t('my_quote.fail').toUpperCase();
};
const initData = (num: number) => {
  ruleData.value = rule[num];
  ruleViewData.value = ruleView;
};

const renderGroupType = () => {
  function applyRuleValue(ruleValue) {
    ruleValue.hotelLog.forEach((hotel) => {
      hotel.groupTypes = groupTypeList.value?.filter((group) => hotel.groupTypes?.includes(group.sk)).map((data) => (currentLanguage === 'fr' ? data.nameFr : data.name));
    });
  }

  groupTypeManagement.getGroupTypeList().then(async (response) => {
    groupTypeList.value = response.data;
    rule.forEach((ruleValue) => {
      applyRuleValue(ruleValue);
    });
  });
};

const updateGroupType = (option) => {
  rule[option].hotelLog.forEach((hotel) => {
    hotel.groupTypes = groupTypeList.value?.filter((group) => hotel.groupTypes?.includes(group.sk)).map((data) => (currentLanguage === 'fr' ? data.nameFr : data.name));
  });
};

const openModalViewRunRule = (isFlightRule, value) => {
  const dialogRef = dialog.open(PreViewRuleModal, {
    props: {
      header: `${t('my_quote.rule_view')}`,
      style: {
        width: '95vw'
      },
      breakpoints: {
        '960px': '75vw',
        '640px': '90vw'
      },
      modal: true,
      maximizable: true
    },
    data: {
      ruleData: value,
      isFlightRule,
      groupTypeList
    },
    emits: {
      onClose: () => {
        dialogRef.close();
      }
    }
  });
};

const handleCheckAvailFlight = (response) => {
  openModalViewRunRule(true, response);
};

const handleCheckAvailHotel = (response) => {
  openModalViewRunRule(false, response);
};
const handleUpdateFlightRule = (option) => {
  isLoading.value = true;
  viewRuleStore
    .updateFlightRule(quoteID, option)
    .then((res) => {
      rule[option] = res.rule[option];
      updateGroupType(option);
      initData(option);
      handleCheckAvailFlight(res.rule[option]);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.update_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleUpdateHotelRule = (optionValue) => {
  isLoading.value = true;
  viewRuleStore
    .updateHotelRule(quoteID, optionValue)
    .then((res) => {
      rule[optionValue] = res.rule[optionValue];
      updateGroupType(optionValue);
      initData(optionValue);
      handleCheckAvailHotel(res.rule[optionValue]);
    })
    .catch(() => {
      toast.add({ severity: 'error', summary: '', detail: t('notification.update_error'), life: 5000 });
    })
    .finally(() => {
      isLoading.value = false;
    });
};

const handleCovertData = (status: number) => {
  return status === -1 ? status : !status;
};
onBeforeMount(() => {
  initData(0);
  renderGroupType();
});
</script>
<template>
  <LoadingSpinner v-if="isLoading" />
  <div class="view-rule-package">
    <div v-if="rule?.length > 0">
      <div class="option">
        <div class="lead-option">{{ $t('common.package') }}</div>
        <div class="detail-option flex justify-content-between flex-wrap pr-4">
          <div>
            <div class="gateway font-bold mb-2 mt-2">
              <div class="flex gap-3">
                <div class="min-w-label">{{ $t('common.gateway') }}:</div>
                <div class="font-normal">{{ ruleData.gate }} ({{ ruleData.gateCode }})</div>
              </div>
            </div>
            <div class="destination font-bold mb-2">
              <div class="flex gap-3">
                <div class="min-w-label">{{ $t('common.destination') }}:</div>
                <div class="font-normal">{{ ruleData.dest }} ({{ ruleData.airportCode }})</div>
              </div>
            </div>
            <div class="hotel font-bold mb-2">
              <div class="flex gap-3">
                <div class="min-w-label">{{ $t('worksheet.hotel') }}:</div>
                <div class="font-normal white-space-pre-line">{{ getHotel(ruleData) }}</div>
              </div>
            </div>
            <div class="hotel-room-cat font-bold mb-2">
              <div class="flex gap-3">
                <div class="min-w-label">{{ $t('worksheet.hotel_room_category') }}:</div>
                <div class="font-normal white-space-pre-line">{{ getRoom(ruleData) }}</div>
              </div>
            </div>
            <div class="departure-date font-bold mb-2">
              <div class="flex gap-3">
                <div class="min-w-label">{{ $t('worksheet.departure_date') }}:</div>
                <div class="font-normal">{{ formatDate(ruleData.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}</div>
              </div>
            </div>
          </div>
          <div>
            <div class="adults font-bold mt-2 mb-2">
              <div class="flex">
                <div class="min-w-label">{{ $t('common.adults') }}:</div>
                <div class="font-normal">{{ ruleData.adtNo }}</div>
              </div>
            </div>
            <div class="child font-bold mb-2">
              <div class="flex">
                <div class="min-w-label">{{ $t('common.children') }}:</div>
                <div class="font-normal">{{ ruleData.chdNo }}</div>
              </div>
            </div>
            <div class="total font-bold mb-2">
              <div class="flex">
                <div class="min-w-label">{{ $t('worksheet.total_passengers') }}:</div>
                <div class="font-normal">{{ ruleData.adtNo + ruleData.chdNo }}</div>
              </div>
            </div>
            <div class="number_room font-bold mb-2">
              <div class="flex">
                <div class="min-w-label">{{ $t('advance_quote.form.number_of_room') }}:</div>
                <div class="font-normal">{{ ruleData.numOfRooms }}</div>
              </div>
            </div>
            <div class="duration font-bold mb-2">
              <div class="flex">
                <div class="min-w-label">{{ $t('worksheet.duration') }}:</div>
                <div class="font-normal">{{ ruleData.duration }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="mt-2 ml-4">
        <ul>
          <li class="text-lg">
            {{ $t('rule.group_identification.title') }}
            <ul>
              <li class="text-lg mt-1 multiple_option">
                {{ $t('rule.group_identification.multiple_option') }} <span :class="renderClass(ruleViewData?.multiOptionForSamePassenger)">({{ renderStatusYesNo(ruleViewData?.multiOptionForSamePassenger) }})</span>
              </li>
              <li class="text-lg mt-1 travel_together">
                {{ $t('rule.group_identification.travel_together') }} <span :class="renderClass(ruleViewData?.optionTravellingTogetherAsGroup)">({{ renderStatusYesNo(ruleViewData?.optionTravellingTogetherAsGroup) }})</span>
              </li>
              <ul>
                <li class="text-lg mt-1 check_total">
                  {{ $t('rule.group_identification.check_total') }} <span :class="renderClass(ruleViewData?.totalPassengerAgainstMaxRoom)">({{ renderStatus(ruleViewData?.totalPassengerAgainstMaxRoom) }})</span>
                </li>
              </ul>
              <ul>
                <li class="text-lg mt-1 same_across_all_option">
                  {{ $t('rule.group_identification.same_across_all_option') }}
                  <span :class="renderClass(ruleViewData?.gatewayDestinationDepartureDateSameAllOptions)">({{ renderStatusYesNo(ruleViewData?.gatewayDestinationDepartureDateSameAllOptions) }})</span>
                </li>
                <ul>
                  <li class="text-lg mt-1 seats_request">
                    {{ $t('rule.group_identification.seats_request') }} <span :class="renderClass(ruleViewData?.totalSRLessThanOrEqualMaxSRFlightRule)">({{ renderStatus(ruleViewData?.totalSRLessThanOrEqualMaxSRFlightRule) }})</span>
                  </li>
                </ul>
              </ul>
            </ul>
          </li>
          <li class="text-lg mt-2 group_type_selected">
            {{ $t('rule.group_type') }} <span>({{ getTextVariant(currentLanguage, ruleData.gType, ruleData.gTypeFr) }})</span>
            <span :class="renderClass(ruleViewData?.groupTypeSelectedAsInstaQuote)">({{ renderStatus(ruleViewData?.groupTypeSelectedAsInstaQuote) }})</span>
          </li>
          <li class="text-lg mt-2 brochure_code">
            {{ $t('rule.brochure_code') }}
            <span v-if="ruleData.brochure">({{ ruleData.brochure }})</span>
            <span :class="renderClass(ruleViewData?.brochureAvailable)">({{ renderStatus(ruleViewData?.brochureAvailable) }})</span>
          </li>
        </ul>
      </div>
      <Tabs :value="0" @update:value="initData($event)">
        <TabList>
          <Tab v-for="(_, index) in rule" :key="index" :value="index">{{ 'OPTION ' + Number(index + 1) }}</Tab>
        </TabList>
        <TabPanels>
          <TabPanel v-for="(option, index) in rule" :key="index" :value="index">
            <div>
              <div>
                <div class="rule-form">
                  <p class="font-bold m-0 text-900 text-lg">{{ $t('rule.quote_form_rule') }}</p>
                  <div class="mt-2 ml-4">
                    <ul>
                      <li class="text-lg">
                        {{ $t('rule.pre_reg_check') }}
                        <ul>
                          <li class="text-lg mt-1 over_dep_date">
                            {{ $t('my_quote.over_dep_date') }} <span :class="renderClass(ruleData.quoteFormRule.departureDate)">({{ renderStatusYesNo(ruleData.quoteFormRule.departureDate) }})</span>
                          </li>
                          <li class="text-lg mt-1 pre_reg_duration_equal_7">
                            {{ $t('rule.pre_reg_duration_equal_7') }} <span :class="renderClass(ruleData.quoteFormRule.pre_reg_duration_equal_7)">({{ renderStatusYesNo(ruleData.quoteFormRule.pre_reg_duration_equal_7) }})</span>
                            <ul>
                              <li class="text-lg mt-1 pre_reg_sr_greater_60">
                                {{ $t('rule.pre_reg_sr_greater_60') }} <span :class="renderClassSpecial(ruleData.quoteFormRule.pre_reg_sr_greater_60)">({{ renderStatusYesNoSpecial(ruleData.quoteFormRule.pre_reg_sr_greater_60) }})</span>
                              </li>
                            </ul>
                          </li>
                          <li class="text-lg mt-1 pre_reg_duration_not_equal_7">
                            {{ $t('rule.pre_reg_duration_not_equal_7') }}
                            <span :class="renderClass(handleCovertData(ruleData.quoteFormRule.pre_reg_duration_equal_7))">({{ renderStatusYesNo(handleCovertData(ruleData.quoteFormRule.pre_reg_duration_equal_7)) }})</span>
                            <ul>
                              <li class="text-lg mt-1">
                                {{ $t('rule.pre_reg_sr_greater_19') }} <span :class="renderClassSpecial(ruleData.quoteFormRule.pre_reg_sr_greater_19)">({{ renderStatusYesNoSpecial(ruleData.quoteFormRule.pre_reg_sr_greater_19) }})</span>
                              </li>
                            </ul>
                          </li>
                        </ul>
                      </li>
                      <li class="text-lg mt-1 room_categories">
                        {{ $t('rule.base_price') }} <span :class="renderClass(ruleData.quoteFormRule.basePriceNotAvailable)">({{ renderStatusYesNo(ruleData.quoteFormRule.basePriceNotAvailable) }})</span>
                      </li>
                      <li class="text-lg mt-1 room_categories">
                        {{ `${$t('rule.margin_safety')} ${option.netMarginTextDisplay ?? ''}` }} <span :class="renderClass(ruleData.quoteFormRule.netMargin ?? -1)">({{ renderStatusYesNo(ruleData.quoteFormRule.netMargin ?? -1) }})</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div class="hotel-log">
                  <div class="heading mb-3 flex align-items-center gap-4">
                    <p class="font-bold text-900 m-0">{{ $t('my_quote.hotel_rule') }}</p>
                    <Tag :severity="getTagSeverity(option?.rStatusHotel)" :value="getTagValue(option?.rStatusHotel)" rounded class="tag-rule"></Tag>
                    <Button v-if="option?.rStatusHotel === null" class="bg-primary" :label="$t('common.buttons.run_rule')" @click="handleUpdateHotelRule(index)" />
                  </div>

                  <div class="hotel-log-list flex flex-col gap-3">
                    <div v-for="(hotelLogDetail, index) in option.hotelLog" :key="hotelLogDetail.code" class="hotel-log-detail">
                      <div class="mb-2 text-lg">{{ $t('common.hotel') }} {{ index + 1 }}</div>
                      <DataTable :value="[hotelLogDetail]" class="w-full" editMode="row" data-key="code" showGridlines>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="hCode" :header="$t('management.hotel.tts_id')">
                          <template #body="{ data }">
                            {{ data.hCode }}
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="code" :header="$t('management.hotel.soft_voyage_id')">
                          <template #body="{ data }">
                            {{ data.code }}
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="name" :header="$t('management.hotel.hotel_name')">
                          <template #body="{ data }">
                            {{ data.name }}
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="chainName" :header="$t('management.hotel.chain_name')">
                          <template #body="{ data }">
                            {{ data.chainName }}
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="destName" :header="$t('common.destination')">
                          <template #body="{ data }">
                            {{ data.destName }}
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="maxRooms" class="text-center">
                          <template #header>
                            <div class="flex flex-col m-auto justify-content-center">
                              <div class="text-center">{{ $t('management.hotel.max_rooms') }}</div>
                              <div class="hidden">
                                <span class="font-bold text-sm">{{ $t('management.hotel.avg_pax_room') + ' : ' + option.hotelLog[0].avgPaxRoom }}</span>
                              </div>
                            </div>
                          </template>
                          <template #body="{ data }">
                            {{ data.maxRooms }}
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="insta" :header="$t('management.hotel.instaquote')">
                          <template #body="{ data, field }">
                            <ToggleSwitch v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" disabled />
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="hotelPreferred.sts" :header="$t('management.hotel.preferred')" class="m-w-128">
                          <template #body="{ data }">
                            <ToggleSwitch disabled v-model="data.hotelPreferred.sts" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
                            <div>
                              {{ data.hotelPreferred?.startDate }}
                              {{ data.hotelPreferred?.endDate }}
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="excludeHotelRoom" :header="$t('management.hotel.exclude_room')">
                          <template #body="{ data, field }">
                            <div class="flex align-items-center gap-2 justify-content-end">
                              <AutoComplete class="mb-1 mt-2 min-w-240" :selectionLimit="200" v-model="data[field]" disabled inputId="inputChip" multiple fluid :typeahead="false" />
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="groupTypes" :header="$t('management.hotel.group_type')">
                          <template #body="{ data, field }">
                            <div class="flex align-items-center gap-2 justify-content-end">
                              <AutoComplete class="mb-1 mt-2 min-w-240" :selectionLimit="200" v-model="data[field]" disabled inputId="inputChip" multiple fluid :typeahead="false" />
                            </div>
                          </template>
                        </TableColumn>
                        <TableColumn :show-filter-operator="false" :show-add-button="false" field="status" :header="$t('common.status')">
                          <template #body="{ data, field }">
                            <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
                          </template>
                        </TableColumn>
                      </DataTable>

                      <div class="hotel-rule mt-4">
                        <div v-if="option.noteRuleHotel" id="hotel-rule-note" class="mt-2 mb-3 ml-4 font-bold text-lg text-900">
                          {{ option.noteRuleHotel }}
                        </div>

                        <div v-if="ruleData.ruleHotelDisplay[index]" :id="`hotel-rule-display-${index}`" class="mt-2 ml-4">
                          <ul>
                            <li v-if="!isNaN(ruleData.ruleHotelDisplay[index].hotelRule)" id="hotel-rule-hotel-rule" class="text-lg mt-1">
                              {{ $t('rule.hotel_rule.hotel_rule') }}
                              <span :class="renderClass(ruleData.ruleHotelDisplay[index].hotelRule)"> ({{ renderStatus(ruleData.ruleHotelDisplay[index].hotelRule) }}) </span>
                            </li>

                            <li v-if="!isNaN(ruleData.ruleHotelDisplay[index].maxNumbersOfRooms)" id="hotel-rule-max-rooms" class="text-lg mt-1">
                              {{ $t('rule.hotel_rule.max_number_of_room') }}
                              <span :class="renderClass(ruleData.ruleHotelDisplay[index].maxNumbersOfRooms)"> ({{ renderStatus(ruleData.ruleHotelDisplay[index].maxNumbersOfRooms) }}) </span>
                            </li>

                            <li v-if="!isNaN(ruleData.ruleHotelDisplay[index].exRoomCategory)" id="hotel-rule-ex-room-category" class="text-lg mt-1">
                              {{ $t('rule.hotel_rule.ex_room_category') }}
                              <span :class="renderClass(ruleData.ruleHotelDisplay[index].exRoomCategory)"> ({{ renderStatus(ruleData.ruleHotelDisplay[index].exRoomCategory) }}) </span>
                            </li>

                            <li v-if="!isNaN(ruleData.ruleHotelDisplay[index].preferred)" id="hotel-rule-preferred" class="text-lg mt-1">
                              {{ $t('rule.hotel_rule.preferred') }}
                              <span :class="renderClass(ruleData.ruleHotelDisplay[index].preferred)"> ({{ renderStatus(ruleData.ruleHotelDisplay[index].preferred) }}) </span>
                            </li>

                            <li v-if="!isNaN(ruleData.ruleHotelDisplay[index].roomPricing)" id="hotel-rule-room-pricing" class="text-lg mt-1">
                              {{ $t('rule.hotel_rule.roomPricing') }}
                              <span :class="renderClass(ruleData.ruleHotelDisplay[index].roomPricing)"> ({{ renderStatus(ruleData.ruleHotelDisplay[index].roomPricing) }}) </span>

                              <ul v-if="ruleData.ruleHotelDisplay[index].roomPricing === 0 && ruleData.roomPricingErrMessage" id="hotel-rule-room-pricing-error">
                                <li id="hotel-rule-room-pricing-error-message">{{ $t('common.error') }}: {{ ruleData.roomPricingErrMessage }}</li>
                              </ul>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="flight-log">
                  <div class="heading mt-5 mb-3 flex align-items-center gap-4">
                    <p class="font-bold m-0 text-900">{{ $t('my_quote.flight_rule') }}</p>
                    <Tag :severity="getTagSeverity(option?.rStatusFlight)" :value="getTagValue(option?.rStatusFlight)" rounded class="tag-rule"></Tag>
                    <Button v-if="option?.rStatusFlight === null" class="bg-primary" :label="$t('common.buttons.run_rule')" @click="handleUpdateFlightRule(index)" />
                  </div>
                  <div class="flight-log-detail">
                    <DataTable v-model:expandedRows="expandedRows[index]" :value="option.flightLog" class="w-full" editMode="row" showGridlines data-key="id">
                      <TableColumn :expander="true" class="w-3rem" />
                      <TableColumn field="ruleType" :header="$t('management.package.type')" :showAddButton="false" :showFilterOperator="false">
                        <template #body="{ data, field }">
                          <span class="long-text" v-tooltip="data[field]">
                            {{ data[field] }}
                          </span>
                        </template>
                      </TableColumn>
                      <TableColumn field="gateCode" :showAddButton="false" :showFilterOperator="false">
                        <template #header>
                          <div class="flex flex-col">
                            <span>{{ $t('management.package.gateway') }}</span>
                            <span>({{ $t('management.package.code') }})</span>
                          </div>
                        </template>
                        <template #body="{ data }">
                          <div class="flex flex-col">
                            <span>{{ data.gateName }}</span>
                            <span v-if="data.gateCode">({{ data.gateCode }})</span>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn field="desCode" :showAddButton="false" :showFilterOperator="false">
                        <template #header>
                          <div class="flex flex-col">
                            <span>{{ $t('management.package.destination') }}</span>
                            <span>({{ $t('management.package.code') }})</span>
                          </div>
                        </template>
                        <template #body="{ data }">
                          <div class="flex flex-col">
                            <span>{{ data.desName }}</span>
                            <span v-if="data.desCode">({{ data.desCode }})</span>
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn class="white-space-nowrap" field="minDuration" :header="$t('management.package.min_duration')" :showAddButton="false" :showFilterOperator="false" />
                      <TableColumn class="white-space-nowrap" field="maxDuration" :header="$t('management.package.max_duration')" :showAddButton="false" :showFilterOperator="false" />
                      <TableColumn class="white-space-nowrap" field="minPax" :header="$t('management.package.min_pax')" :showAddButton="false" :showFilterOperator="false" />
                      <TableColumn class="white-space-nowrap" field="maxPax" :header="$t('management.package.max_pax')" :showAddButton="false" :showFilterOperator="false" />
                      <TableColumn class="white-space-nowrap" field="minFClass" :showAddButton="false" :showFilterOperator="false">
                        <template #header>
                          <span>{{ $t('management.package.min_f_class') }} ( % )</span>
                        </template>
                        <template #body="{ data, field }">
                          <span v-if="data[field] !== null">{{ data[field] }} %</span>
                        </template>
                      </TableColumn>
                      <TableColumn class="white-space-nowrap" field="maxYClass" :showAddButton="false" :showFilterOperator="false">
                        <template #header>
                          <span>{{ $t('management.package.max_y_class') }} ( % )</span>
                        </template>
                        <template #body="{ data, field }">
                          <span v-if="data[field] !== null">{{ data[field] }} %</span>
                        </template>
                      </TableColumn>
                      <TableColumn class="white-space-nowrap" field="depDateStart" :header="$t('management.package.departure_date_start')" :showAddButton="false" :showFilterOperator="false">
                        <template #body="{ data }">
                          {{ formatDate(data.depDateStart, 'dd-LLL-yyyy', false, currentLanguage) }}
                        </template>
                      </TableColumn>
                      <TableColumn class="white-space-nowrap" field="depDateEnd" :header="$t('management.package.departure_date_end')" :showAddButton="false" :showFilterOperator="false">
                        <template #body="{ data }">
                          {{ formatDate(data.depDateEnd, 'dd-LLL-yyyy', false, currentLanguage) }}
                        </template>
                      </TableColumn>
                      <TableColumn class="white-space-nowrap" field="minCost" :header="$t('management.package.minimum_cost')" :showAddButton="false" :showFilterOperator="false" />
                      <TableColumn class="cost-rule" field="costRule" :header="$t('management.package.apply_cost_rule')" :showAddButton="false" :showFilterOperator="false">
                        <template #body="{ data }">
                          <div class="flex justify-content-center">
                            <Checkbox :disabled="true" v-model="data.costRule" :binary="true" />
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn class="rule-connector" field="connectorRule" :header="$t('management.package.apply_rule_connector')" :showAddButton="false" :showFilterOperator="false">
                        <template #body="{ data }">
                          <div class="flex justify-content-center">
                            <Checkbox :disabled="true" v-model="data.connectorRule" :binary="true" />
                          </div>
                        </template>
                      </TableColumn>
                      <TableColumn :show-filter-operator="false" :show-add-button="false" field="ruleStatus" :header="$t('common.status')">
                        <template #body="{ data, field }">
                          <ToggleSwitch disabled v-model="data[field]" :trueValue="STATUS_OPEN" :falseValue="STATUS_CLOSED" />
                        </template>
                      </TableColumn>
                      <template #expansion="slotProps">
                        <div class="p-3">
                          <h5>{{ $t('common.flight_details') }}</h5>
                          <DataTable :value="slotProps.data.rClass" class="w-full" showGridlines>
                            <TableColumn field="flightNo" :header="$t('rule.flight_rule.flight_number')" dataType="string" class="min-w-10rem">
                              <template #body="{ data }">
                                {{ data.flightNo }}
                              </template>
                            </TableColumn>
                            <TableColumn field="gate" :header="$t('worksheet.departure_city')" dataType="string" class="min-w-10rem">
                              <template #body="{ data }">
                                {{ data.gate }}
                              </template>
                            </TableColumn>
                            <TableColumn field="dest" :header="$t('worksheet.arrival_city')" dataType="string" class="min-w-10rem">
                              <template #body="{ data }">
                                {{ data.dest }}
                              </template>
                            </TableColumn>
                            <TableColumn field="depDate" :header="$t('worksheet.dep_date')" class="min-w-10rem">
                              <template #body="{ data }">
                                {{ formatDate(data?.depDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                              </template>
                            </TableColumn>
                            <TableColumn field="depTime" :header="$t('worksheet.dep_time')" dataType="date" class="min-w-10rem">
                              <template #body="{ data }">
                                {{ data.depTime }}
                              </template>
                            </TableColumn>
                            <TableColumn field="arrDate" :header="$t('worksheet.arrival_date')" dataType="date" class="min-w-10rem">
                              <template #body="{ data }">
                                {{ formatDate(data.arrDate, 'dd-LLL-yyyy', false, currentLanguage) }}
                              </template>
                            </TableColumn>
                            <TableColumn field="arrTime" :header="$t('worksheet.arrival_time')" dataType="date" class="min-w-10rem">
                              <template #body="{ data }">
                                {{ data.arrTime }}
                              </template>
                            </TableColumn>
                            <TableColumn field="fClass" :header="$t('common.class')">
                              <template #body="{ data }">
                                <span>{{ data.fClass }}</span>
                              </template>
                            </TableColumn>
                          </DataTable>
                          <div v-if="ruleData.flightNoValid !== false" class="classes-available mt-4">
                            <div class="mb-1">
                              <div class="classes-available-header flex justify-content-between cursor-pointer">
                                <div class="flex align-items-center gap-10">
                                  <div>{{ $t('worksheet.class_available') }}</div>
                                </div>
                              </div>
                              <div v-show="slotProps.data.rClass">
                                <div class="classes-available-detail">
                                  <div class="outbound-detail flex w-full" v-for="(flight, flightIndex) in slotProps.data.rClass" :key="'flight' + flightIndex">
                                    <div class="title w-2 p-3">{{ $t(`worksheet.${flight.flight?.toLowerCase()}`) }}</div>
                                    <div class="title w-2 p-3">{{ flight.flightNo }}</div>
                                    <div class="flex flex-wrap pb-5 gap-1 w-10">
                                      <div class="p-2" v-for="(value, property) in flight.rClass" :key="'outbound' + property">
                                        <div class="box-bodder">{{ property }}</div>
                                        <div class="box-bodder">{{ value }}</div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>
                    </DataTable>
                  </div>
                </div>
                <div class="flight-rule mt-4">
                  <div v-if="ruleData.flightNoValid === false">
                    <div class="mt-2 mb-4 ml-4 font-bold text-lg text-900">{{ $t('rule.flight_no_invalid') }}</div>
                  </div>
                  <div v-else>
                    <div v-if="ruleData.ruleFlightDisplay.flightEmpty" class="mt-2 mb-4 ml-4 font-bold text-lg text-900">{{ $t('rule.flight_rule.flight_empty') }}</div>
                    <div v-if="ruleData.ruleFlightDisplay.flightRuleNotFound" class="mt-2 mb-4 ml-4 font-bold text-lg text-900">{{ $t('rule.flight_rule.rule_not_found') }}</div>
                    <div v-if="ruleData.ruleFlightDisplay.flightNotValidWorksheet" class="mt-2 mb-4 ml-4 font-bold text-lg text-900">{{ $t('rule.flight_rule.flight_not_valid') }}</div>
                  </div>
                  <div class="mt-2 ml-4">
                    <ul>
                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.flightRule)" id="flight-rule-retrieved" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.flight_rule') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.flightRule)"> ({{ renderStatus(ruleData.ruleFlightDisplay.flightRule) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.FBR)" id="flight-rule-fbr" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.fbr') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.FBR)"> ({{ renderStatusYesNo(ruleData.ruleFlightDisplay.FBR) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.FBRDepartureDate)" id="flight-rule-fbr-departure-date" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.fbr_departure_date') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.FBRDepartureDate)"> ({{ renderStatus(ruleData.ruleFlightDisplay.FBRDepartureDate) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.FBRSeatsInRule)" id="flight-rule-fbr-seats-in-rule" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.fbr_seats_in_rule') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.FBRSeatsInRule)"> ({{ renderStatus(ruleData.ruleFlightDisplay.FBRSeatsInRule) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.FBRSeatsFClass)" id="flight-rule-fbr-seats-f-class" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.fbr_seats_f_class') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.FBRSeatsFClass)"> ({{ renderStatus(ruleData.ruleFlightDisplay.FBRSeatsFClass) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.seats)" id="flight-rule-seats" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.seats') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.seats)"> ({{ renderStatus(ruleData.ruleFlightDisplay.seats) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.FBRSeatsYClass)" id="flight-rule-fbr-seats-y-class" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.fbr_seats_y_class_number', { number: option.flightLog[0].maxYClass }) }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.FBRSeatsYClass)"> ({{ renderStatus(ruleData.ruleFlightDisplay.FBRSeatsYClass) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.departureDate)" id="flight-rule-departure-date" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.departure_date') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.departureDate)"> ({{ renderStatus(ruleData.ruleFlightDisplay.departureDate) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.duration)" id="flight-rule-duration" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.duration') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.duration)"> ({{ renderStatus(ruleData.ruleFlightDisplay.duration) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.fClass)" id="flight-rule-f-class" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.f_class') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.fClass)"> ({{ renderStatus(ruleData.ruleFlightDisplay.fClass) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.fClass80)" id="flight-rule-f-class-80" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.f_class_number', { number: option.flightLog[0].minFClass }) }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.fClass80)"> ({{ renderStatus(ruleData.ruleFlightDisplay.fClass80) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.yClass)" id="flight-rule-y-class" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.y_class_number', { number: option.flightLog[0].maxYClass }) }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.yClass)"> ({{ renderStatus(ruleData.ruleFlightDisplay.yClass) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.sharedRisk)" id="flight-rule-shared-risk" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.shared_risk') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.sharedRisk)"> ({{ renderStatusYesNo(ruleData.ruleFlightDisplay.sharedRisk) }}) </span>
                      </li>

                      <li v-if="!isNaN(ruleData.ruleFlightDisplay.flightCost)" id="flight-rule-flight-cost" class="text-lg mt-1">
                        {{ $t('rule.flight_rule.flight_cost') }}
                        <span :class="renderClass(ruleData.ruleFlightDisplay.flightCost)"> ({{ renderStatus(ruleData.ruleFlightDisplay.flightCost) }}) </span>
                        <ul v-if="ruleData.ruleFlightDisplay.flightCost !== -1" id="flight-rule-apply-cost-rule-list">
                          <li id="flight-rule-apply-cost-rule" class="text-lg mt-1 over_dep_date">
                            {{ $t('management.package.apply_cost_rule') }}
                            <span :class="renderClass(ruleData.ruleFlightDisplay.applyCost)"> ({{ renderStatus(ruleData.ruleFlightDisplay.applyCost) }}) </span>
                          </li>
                        </ul>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </div>
    <div v-else class="text-center">
      <h4 class="ml-2">{{ $t('common.nodata') }}</h4>
    </div>
  </div>
</template>
<style lang="scss" scoped>
:deep() {
  .p-chips {
    width: 100%;
  }
  .p-chips-multiple-container {
    width: 100%;
    gap: 0.1rem;
    min-height: 42px;
  }
  .p-chips-input-token,
  .p-chips-token-icon {
    display: none;
  }
  .min-w-240 {
    min-width: 240px !important;
    width: 100%;
  }
}
</style>
